import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocalStorage } from 'angular-web-storage';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { timeout } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ListResponse } from '../../models/list-response';
import { APIListResponse } from '../../models/api-list-response';
import { StaffMemberByPIN } from '../../models/time-tracking';
import { DatabaseHealth } from '../../models/database-health';
import { generateDownload, getHttpParamsForFilters } from '../../utils';
import { PinModalComponent } from '../../components/modals/pin-modal/pin-modal.component';
import { SimpleMessageModalComponent } from '../../components/modals/simple-message-modal/simple-message-modal.component';
import { DatabaseMaintenanceModalComponent } from '../../components/modals/database-maintenance-modal/database-maintenance-modal.component';


// The body marker capitan-server stamps on a response served while its database
// is unreachable (PRO-1361). Keyed on the marker, not a bare 503, so a proxy 503
// can't raise a false maintenance modal; the body rather than a custom header
// because headers aren't readable cross-origin without Access-Control-Expose-Headers.
const DATABASE_UNAVAILABLE_ERROR_CODE = 'database_unavailable';

// How long a database health check may run before it counts as a failure.
const HEALTH_CHECK_TIMEOUT_MS = 8000;

// How long a request must have been in flight before a status 0 is read as
// Capitan giving up on it (gunicorn worker timeout 30s, ALB/nginx 60s) rather
// than as Capitan being unreachable (PRO-1465). Those kills carry no CORS
// headers, so elapsed time is the only signal; 20s sits below the earliest
// ceiling, above every fast-failing cause, and equals the interactive metrics
// budget (METRICS_QUERY_TIMEOUT_S).
const REQUEST_TIMEOUT_ELAPSED_MS = 20000;


/** What a request helper knows about a failed request, beyond the error itself. */
export interface RequestFailureContext {
  // When the request actually went out — NOT when the helper was called, which
  // may have sat on an open PIN modal (a slow human is not a slow server).
  startedAt: number;

  // Whether the request went to the reporting host; only affects the timeout wording.
  reporting?: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class ApiService {
  // The token is placed in browser storage in order to persist the user login.
  @LocalStorage() public token: string = null;

  @LocalStorage() public otpToken: string = null;

  // Whether the app is in PIN mode, in which case the token can only be used
  // for safe HTTP requests.
  @LocalStorage() public inPinMode = false;

  // The PIN is stored temporarily after it is entered.
  public pin: string = null;

  // The number of seconds to retain an entered PIN, when in PIN mode. This is
  // a parameter so that the Organization Config Service can set it without
  // introducing a circular dependency.
  public pinTimeoutSeconds: number = environment.default_pin_timeout_seconds;

  // Whether the PIN modal is currenctly open. If true, any attempted unsafe
  // operations will return pinHeadersPromise rather than reopening the modal.
  private pinModalIsOpen = false;

  // Contains the promise returning headers subsequent to the currently-open
  // PIN modal completing. This allows multiple concurrent requests to execute
  // when the modal completes, rather then opening up new modals.
  private pinHeadersPromise: Promise<HttpHeaders>;

  private clearPinRequestCount = 0;

  // Whether the network error warning modal is currently open. If true, any
  // further network errors will not cause a new modal to open.
  private networkErrorModalIsOpen = false;

  // Guards against stacking a second modal: during an outage every in-flight request fails at once.
  private databaseMaintenanceModalIsOpen = false;

  /**
   * A service providing an interface to the API, handling authentication as
   * well as standard syntax.
   */
  constructor(
    private http: HttpClient,
    private modalService: NgbModal
  ) {}

  getStaffMemberByPin(pin: string): Promise<StaffMemberByPIN> {
    return this.getFromAPI<StaffMemberByPIN>(
      'gym-endpoints/time-tracking/get-staff-member-by-pin/' + pin + '/'
    );
  }

  /**
   * Get a page of results from an API collection.
   *
   * @Model - The type of object of which a page of results will be obtained.
   * @ModelFilters - The type representing a set of filters on the collection.
   *
   * @param apiPath - The URL path of the API collection.
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of items per page.
   * @param filters - The set of filters to apply to the items.
   * @param isOperational - Indicates to the API server that this is an
   *     "operational" query, which will use the primary database (as opposed
   *     to the replica).
   * @param background - A request the user didn't initiate; see
   *     withFailureHandling.
   * @return The page of results.
   */
  getCollection<Model, ModelFilters>(apiPath: string,
                                     pageNumber?: number,
                                     pageSize?: number,
                                     filters?: ModelFilters,
                                     isOperational?: boolean,
                                     background?: boolean): Promise<ListResponse<Model>> {
    let params = getHttpParamsForFilters(filters);

    if ((pageNumber === undefined) || (pageNumber === null)) {
      pageNumber = 1;
    }
    params = params.set('page', pageNumber.toString());
    if (pageSize) {
      params = params.set('page_size', pageSize.toString());
    }
    if (isOperational) {
      params = params.set('is_operational', 'true');
    }


    // If the isOperational parameter is explicitly provided to be false,
    // direct the request to the reporting API.
    let useReportingApi = false;
    if (isOperational === false) {
      useReportingApi = true;
    }

    return this.getFromAPI<APIListResponse<Model>>(
      apiPath,
      params,
      useReportingApi,
      background
    )
      .then((data: APIListResponse<Model>) => {
        return {
          results: data.results,
          count: data.count,
          nextPage: data.next ? pageNumber + 1 : null,
          previousPage: data.previous ? pageNumber - 1 : null,
        };
      });
  }

  /**
   * Get a page of results from an API collection. This version of getCollection
   * has no filters.
   *
   * @Model - The type of object of which a page of results will be obtained.
   *
   * @param apiPath - The URL path of the API collection.
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of items per page.
   * @return The page of results.
   */
  getCollectionNoFilters<Model>(apiPath: string,
                                pageNumber?: number,
                                pageSize?: number): Promise<ListResponse<Model>> {
    let params = new HttpParams();

    if (!pageNumber) {
      pageNumber = 1;
    }

    params = params.set('page', pageNumber.toString());
    if (pageSize) {
      params = params.set('page_size', pageSize.toString());
    }

    return this.getFromAPI<APIListResponse<Model>>(
      apiPath,
      params
    )
      .then((data: APIListResponse<Model>) => {
        return {
          results: data.results,
          count: data.count,
          nextPage: data.next ? pageNumber + 1 : null,
          previousPage: data.previous ? pageNumber - 1 : null,
        };
      });
  }

  /**
   * Get an individual item from the API.
   *
   * @Model - The type of object of which an item will be obtained.
   *
   * @param apiPath - The URL path of the API collection.
   * @param id - The database ID of the item.
   * @param background - A request the user didn't initiate; see
   *     withFailureHandling.
   * @return The item.
   */
  getItem<Model>(apiPath: string,
                 id: number | string,
                 background?: boolean): Promise<Model> {
    return this.getFromAPI<Model>(
      apiPath + id.toString() + '/',
      undefined,
      undefined,
      background
    );
  }

  getFromAPI<Model>(apiPath: string,
                    params?: HttpParams,
                    useReportingApi?: boolean,
                    background?: boolean): Promise<Model> {
    if (!params) {
      params = new HttpParams();
    }

    let baseUrl = environment.api_url;
    if (useReportingApi) {
      baseUrl = environment.reporting_url;
    }

    return this.getRequestHeaders(true)
      .then(headers => {
        return this.withFailureHandling(
          () => this.http
            .get<Model>(
              baseUrl + apiPath,
              {
                headers,
                params
              }
            )
            .toPromise(),
          {reporting: useReportingApi, background}
        );
      });
  }

  postToAPI<Model>(apiPath: string,
                   body: any,
                   useReportingApi?: boolean,
                   background?: boolean,
                   noContentType?: boolean): Promise<Model> {
    let baseUrl = environment.api_url;
    if (useReportingApi) {
      baseUrl = environment.reporting_url;
    }

    return this.getRequestHeaders(false)
      .then(headers => {
        if (noContentType) {
          headers = headers.delete('Content-Type');
        }

        return this.withFailureHandling(
          () => this.http
            .post<Model>(
              baseUrl + apiPath,
              body,
              {headers}
            )
            .toPromise(),
          {clearPinOn403: true, reporting: useReportingApi, background}
        );
      });
  }

  postToAPIRawURL<Model>(url: string,
                         body: any): Promise<Model> {
    return this.getRequestHeaders(false)
      .then(headers => {
        return this.withFailureHandling(
          () => this.http
            .post<Model>(
              url,
              body,
              {headers}
            )
            .toPromise(),
          {clearPinOn403: true}
        );
      });
  }

  putToAPI<Model>(url: string,
                  body: any): Promise<Model> {
    return this.getRequestHeaders(false)
      .then(headers => {
        return this.withFailureHandling(
          () => this.http
            .put<Model>(
              url,
              body,
              {headers}
            )
            .toPromise(),
          {clearPinOn403: true}
        );
      });
  }

  patchToAPI<Model>(url: string,
                    body: any,
                    noContentType?: boolean): Promise<Model> {
    return this.getRequestHeaders(false)
      .then(headers => {
        if (noContentType) {
          headers = headers.delete('Content-Type');
        }

        return this.withFailureHandling(
          () => this.http
            .patch<Model>(
              url,
              body,
              {headers}
            )
            .toPromise(),
          {clearPinOn403: true}
        );
      });
  }

  deleteFromAPI<Model>(url: string): Promise<Model> {
    return this.getRequestHeaders(false)
      .then(headers => {
        return this.withFailureHandling(
          () => this.http
            .delete<Model>(
              url,
              {headers}
            )
            .toPromise(),
          {clearPinOn403: true}
        );
      });
  }

  downloadFromApi(apiPath: string, downloadFilename: string): Promise<void> {
    return this.getRequestHeaders(true)
      .then(headers => {
        return this.http.get(
          environment.api_url + apiPath,
          {
            headers,
            responseType: 'blob'
          }
        )
          .toPromise()
          .then(blob => {
            generateDownload(blob, downloadFilename);

            return Promise.resolve();
          });
      });
  }

  /**
   * Run a request with the shared post-failure bookkeeping. `clearPinOn403` is
   * off by default: a safe GET sends no PIN, so its 403 says nothing about the
   * stored PIN. `background` marks a request the user didn't initiate: a
   * status-0 failure isn't worth a modal, but PIN clearing and the maintenance
   * modal still run since those are about state, not attention.
   */
  private withFailureHandling<Model>(
    makeRequest: () => Promise<Model>,
    options: { clearPinOn403?: boolean, reporting?: boolean, background?: boolean } = {}
  ): Promise<Model> {
    const context: RequestFailureContext = {
      startedAt: Date.now(),
      reporting: options.reporting
    };

    return makeRequest()
      .catch(reason => {
        if (options.clearPinOn403) {
          this.clearPinIf403(reason);
        }
        if (!options.background) {
          this.openAlertModalIfNetworkError(reason, context);
        }
        this.openMaintenanceModalIfDatabaseOutage(reason);
        return Promise.reject(reason);
      });
  }

  /**
   * The auth headers for a request, prompting for the PIN when needed. Public
   * for the Chart Assistant's raw-`fetch` SSE turn (HttpClient buffers), which
   * must authenticate the same way.
   */
  public getRequestHeaders(safeRequest: boolean): Promise<HttpHeaders> {
    if (this.inPinMode) {
      if (safeRequest) {
        return Promise.resolve(
          environment.api_headers.append(
            'Authorization',
            'TokenAndPin ' + this.token
          )
        );
      } else {
        if (this.pin !== null) {
          return Promise.resolve(
            environment.api_headers.append(
              'Authorization',
              'TokenAndPin ' + this.token + ' ' + this.pin
            )
          );
        } else {
          if (this.pinModalIsOpen) {
            return this.pinHeadersPromise;
          } else {
            return this.getPinAndReturnHeaders();
          }
        }
      }
    } else {
      return Promise.resolve(
        environment.api_headers.append(
          'Authorization',
          'Token ' + this.token
        )
      );
    }
  }

  private getPinAndReturnHeaders(): Promise<HttpHeaders> {
    this.pinHeadersPromise = new Promise<HttpHeaders>((resolve, reject) => {
      this.pinModalIsOpen = true;
      const modalRef = this.modalService.open(PinModalComponent);
      modalRef.componentInstance.verifyPinFunction = (pin: string) => {
        return this.getStaffMemberByPin(pin).then();
      };
      modalRef.componentInstance.pinCallback = (pin: string) => {
        this.pin = pin;

        this.clearPinRequestCount += 1;
        const thisRequestCount = this.clearPinRequestCount;
        setTimeout(
          () => {
            if (thisRequestCount === this.clearPinRequestCount) {
              this.pin = null;
            }
          },
          this.pinTimeoutSeconds * 1000
        );
      };
      modalRef.componentInstance.closedCallback = () => {
        this.pinModalIsOpen = false;
        if (this.pin !== null) {
          resolve(
            environment.api_headers.append(
              'Authorization',
              'TokenAndPin ' + this.token + ' ' + this.pin
            )
          );
        } else {
          // Reject with a 403 to indicate that the user does not have
          // permission to make the request without entering a PIN.
          reject({status: 403});
        }
      };
    });

    return this.pinHeadersPromise;
  }

  /**
   * The post-failure bookkeeping the helpers run in their `catch`, exposed for
   * the Chart Assistant's raw `fetch`: without it a rejected PIN is never
   * cleared and the next send replays it instead of re-prompting.
   */
  public handleRequestFailure(reason: { status: number },
                              context?: RequestFailureContext) {
    this.clearPinIf403(reason);
    this.openAlertModalIfNetworkError(reason, context);
    this.openMaintenanceModalIfDatabaseOutage(reason);
  }

  private clearPinIf403(reason) {
    if (reason.status === 403) {
      // Clear the pin after a brief delay to avoid issues with concurrent
      // requests.
      this.clearPinRequestCount += 1;
      const thisRequestCount = this.clearPinRequestCount;
      setTimeout(
        () => {
          if (thisRequestCount === this.clearPinRequestCount) {
            this.pin = null;
          }
        },
        100
      );
    }
  }

  /**
   * Whether the browser is certain this device is offline. Only
   * `navigator.onLine === false` is trustworthy (`true` means an interface is
   * up, not that anything is reachable), so this may only word a failure
   * detected some other way, never decide whether one happened.
   */
  private isBrowserOffline(): boolean {
    return (typeof navigator !== 'undefined') && (navigator.onLine === false);
  }

  /**
   * Whether a failed request looks like one Capitan gave up on rather than
   * never reached, judged from elapsed time (see REQUEST_TIMEOUT_ELAPSED_MS).
   * No start time = no.
   */
  private isRequestTimeout(context?: RequestFailureContext): boolean {
    return !!context &&
      ((Date.now() - context.startedAt) >= REQUEST_TIMEOUT_ELAPSED_MS);
  }

  private openAlertModalIfNetworkError(reason,
                                       context?: RequestFailureContext) {
    if ((reason.status !== 0) || this.networkErrorModalIsOpen) {
      return;
    }
    this.networkErrorModalIsOpen = true;

    // Status 0 covers a dropped connection, a CORS rejection, a blocked domain,
    // a certificate error and a request killed after running too long, so the
    // wording follows navigator.onLine and elapsed time. Offline is tested
    // first: it has direct evidence, and a request in flight when the
    // connection dropped would otherwise read as a timeout.
    let headerText: string;
    let messageText: string;

    if (this.isBrowserOffline()) {
      headerText = 'No Internet Connection';
      messageText = 'Your device appears to be offline. Please check your ' +
        'network connection and try again.';
    } else if (this.isRequestTimeout(context)) {
      if (context.reporting) {
        headerText = 'Report Timed Out';
        messageText = 'Capitan took too long to build this report and ' +
          'stopped. This is not a connection problem — the report is too ' +
          'large to finish in time. Try a shorter date range or narrower ' +
          'filters, then run it again.';
      } else {
        headerText = 'Request Timed Out';
        messageText = 'Capitan took too long to respond and stopped waiting. ' +
          'This is not a connection problem. Please try again in a moment.';
      }
    } else {
      headerText = 'Could Not Reach Capitan';
      messageText = 'A request could not reach Capitan. Please try again in a moment.';
    }

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = headerText;
    modalRef.componentInstance.messageText = messageText;
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.closedCallback = () => {
      this.networkErrorModalIsOpen = false;
    };
  }

  /** Whether a 503 is capitan-server's own database-unreachable marker. */
  private isDatabaseOutage(reason): boolean {
    if (!reason || (reason.status !== 503)) {
      return false;
    }
    const body = reason.error;
    if (body && (body.error === DATABASE_UNAVAILABLE_ERROR_CODE)) {
      return true;
    }
    // The body arrives as a string when Angular couldn't parse it as JSON.
    return (typeof body === 'string') &&
      (body.indexOf(DATABASE_UNAVAILABLE_ERROR_CODE) !== -1);
  }

  /**
   * Show the blocking maintenance modal when the database is unreachable
   * (PRO-1361). Every site funnels through this service (no HttpInterceptors),
   * so this one seam covers the Staff Site and both kiosks.
   */
  private openMaintenanceModalIfDatabaseOutage(reason) {
    if (!this.isDatabaseOutage(reason) || this.databaseMaintenanceModalIsOpen) {
      return;
    }
    this.databaseMaintenanceModalIsOpen = true;

    const modalRef = this.modalService.open(
      DatabaseMaintenanceModalComponent,
      // Non-dismissible: on an unattended kiosk a dismissed modal leaves a broken screen.
      {backdrop: 'static', keyboard: false}
    );
    modalRef.componentInstance.checkHealthFunction = () => this.checkDatabaseHealth();
    modalRef.componentInstance.closedCallback = () => {
      this.databaseMaintenanceModalIsOpen = false;
    };
  }

  /**
   * Check the database via the unauthenticated health endpoint. Only the API's
   * own `database_unavailable` marker is `down`; every other failure is
   * `unknown`, so losing contact with the API is never reported as a database
   * outage. Bypasses the request helpers: no auth headers (the PIN modal), and
   * no network-error modal stacked on the undismissable maintenance modal.
   */
  public checkDatabaseHealth(): Promise<DatabaseHealth> {
    return this.http
      .get(environment.api_url + 'health/db/')
      // Must always settle, or a hung request stalls the modal's poll loop; kept
      // below the modal's poll interval so checks can't overlap.
      .pipe(timeout(HEALTH_CHECK_TIMEOUT_MS))
      .toPromise()
      .then<DatabaseHealth>(() => 'up')
      .catch<DatabaseHealth>(
        reason => this.isDatabaseOutage(reason) ? 'down' : 'unknown'
      );
  }
}
